import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { TabStrip } from "./TabStrip";
import KnxComObjectTable from "./KnxComObjectTable";

import { ChannelType } from "../models/ChannelType";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import KnxSceneTable from "./KnxSceneTable";
import Device from "../models/Device";
import TabGeneralDevice from "./TabGeneralDevice";
import BaseObject from "../models/BaseObject";
import Button from "../models/Button";
import KnxButtonTable from "./KnxButtonTable/KnxButtonTable";

import Logger from "../Logger";
import PdfViewer from "./PdfViewer";
import { isUiModeDeveloper, isUiModeProductAdmin, uiModeWatcher } from "../uiMode";
import { channelMethode } from "../models/SettingsEnums";
import DetermineTemplateComparbility from "./DetermineTemplateComparbility";
import { useRedraw } from "../models/useRerender";

const logger = new Logger();

import { ErrorBoundary } from "react-error-boundary";
import KnxDataAnalyser from "./KnxDataAnalyser";
import Browser from "../browser";
import AllSignalsOfTemplate from "./AllSignalsOfTemplate";
import TabGeneralTemplate from "./TabGeneralTemplate";
import DeviceTemplate from "../models/DeviceTemplate";
import { itx, noItx } from "../translationItx/itx";
import TabItemData from "../models/TabItemData";
import EstadaSuspense from "./EstadaSuspense";
import ParameterViewerWrapper from "./KnxParameterViewerWrapper";
import PanelLayout from "./PanelLayout/PanelLayoutMain";
import CabinetPropertiesView from "./CabinetPropertiesView";

function ErrorFallback(params: { error: any; resetErrorBoundary: () => void }) {
  const { error, resetErrorBoundary } = params;

  return (
    <div role="alert">
      <p>{itx("deviceTemplatePropertiesView.somethingWentWrong", "Something went wrong:")}</p>
      <pre>{error.message}</pre>
      <button onClick={resetErrorBoundary}>{itx("deviceTemplatePropertieView.tryAgain", "Try again")}</button>
    </div>
  );
}

export function undoUiUpdate(changedProperty: any, changedObject: BaseObject) {
  if (typeof changedProperty === "object") {
    const { source, id, instanceId, chapterId } = changedProperty;
    if (source == "knxProperty-undo") {
      let xid = id;
      if (instanceId != "") {
        xid = instanceId + "-" + id;
      }

      setTimeout(() => {
        const propElm = document.getElementById(xid);
        if (propElm) {
          propElm.style.backgroundColor = "var(--selectedOrange)";
          // propElm.scrollIntoView();
          propElm.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          var parent: any = propElm.closest(".chapter"); // no itx

          let d = 0;
          if (parent) {
            do {
              const child = parent.getElementsByClassName("children")[0] as HTMLElement;
              if (child) {
                (child.style as any).display = null;
              }

              var parent = parent.parentElement?.closest(".chapter"); // no itx
            } while (parent);

            d += 1000;

            const ss = chapterId.split("|");
            for (let i = 0; i < ss.length; i++) {
              (changedObject as any).expanded[chapterId] = true;
            }

            setTimeout(() => {
              propElm.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }, 1000);
          }

          setTimeout(() => {
            (propElm.style as any).backgroundColor = null;
          }, 3000 + d);
        }
      }, 1000);
    }

    return true;
  } else {
    return true;
  }
}

function LazyScriptEditor(props: {
  device: Device;
  instanceId?: string;
  propertyId?: string;
  label?: string;
  standAlone?: boolean;
}) {
  return <EstadaSuspense Component={React.lazy(() => import("./KnxScriptEditor"))} props={props} />;
}

export default function DeviceTemplatePropertiesView(props: { deviceOrDeviceTemplate: Device | DeviceTemplate }) {
  const { deviceOrDeviceTemplate } = props;

  let device: Device = deviceOrDeviceTemplate as Device;

  if (deviceOrDeviceTemplate.className == "DeviceTemplate") {
    const template = deviceOrDeviceTemplate as DeviceTemplate;

    if (template.dummyDevice) {
      device = template.dummyDevice;
      return <DevicePropertiesView device={device} />;
    } else {
      return null;
    }
  } else {
    return <DevicePropertiesView device={device} />;
  }
}

export function DevicePropertiesView(props: { device: Device }) {
  const { device } = props;

  const [activeItem, setActiveItemRaw] = useState<string>(state.getLastPropertiesTab(device.id) ?? "general"); // no itx
  const setActiveItem = (paneId: string) => {
    state.setLastPropertiesTab(device.id, paneId);
    setActiveItemRaw(paneId);
  };
  const [loader, setLoader] = useState(false);
  const redraw = useRedraw();

  const contentRef = useRef<HTMLDivElement>(null);
  const executeAsyncIsRunning = useRef<boolean>(false);
  const mountedRef = useRef(false);

  // useOnChangeWatcher(device, (changedProperty, _changedObject) => {
  //   logger.log(`useOnChangeWatcher(device) changedProperty=${changedProperty}`);

  //   if (changedProperty === "selected" || changedProperty === "childCollection") {
  //     return false;
  //   }

  //   return true;
  // });

  // useOnChangeWatcher(
  //   device.knxInstanceData,
  //   (changedProperty, changedObject) => {
  //     device.updateAllFromKnxMetadata();
  //     return false; // no redaw !!
  //   },
  // );

  useOnChangeWatcher(uiModeWatcher);

  useOnChangeWatcher(device, (changedProperty, _changedObject) => {
    return true;
    //
    // redraw if template is changed
    //
    // return changedProperty == "template" || changedProperty
  });

  async function preloadProductDataAsync() {
    if (!executeAsyncIsRunning.current) {
      executeAsyncIsRunning.current = true;
      try {
        if (device.template && device.template.knxProductData) {
          await device.template.knxProductData.loadXmlData(device);
          if (!mountedRef.current) return;

          await device.updateAllFromKnxMetadata();
          if (!mountedRef.current) return;

          if (device.template?.knxProductData.currentRules) {
            const atleastOneParamChanged = device.template.knxProductData.currentRules.pullAllValues(device, {});
            if (atleastOneParamChanged) {
              await device.updateAllFromKnxMetadata();
              if (!mountedRef.current) return;
            }
          }
        }
      } catch (ex) {
        logger.error(ex);
      } finally {
        executeAsyncIsRunning.current = false;
      }
    }
  }

  useEffect(() => {
    mountedRef.current = true;
    void preloadProductDataAsync();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  //////////////////////////////////////////////////////////////////////////////////

  const actorTypesSelectionElmements: any = [];
  actorTypesSelectionElmements.push(
    <option value={ChannelType.Unknown}>
      {itx("deviceTemplatePropertieView.chooseTypeActor", "Wähle den Type des Aktors")}
    </option>,
  );
  for (const i in ChannelType) {
    const channelType = i;
    actorTypesSelectionElmements.push(<option value={channelType}>{channelType}</option>);
  }

  const bottomTabItems: Array<TabItemData> = [
    new TabItemData(noItx("general"), itx("deviceTemplatePropertieView.general", "Allgemein"), undefined, false),
  ];

  if (device.isButton) {
    if (device.isDecentralActor) {
      bottomTabItems.push(
        new TabItemData(
          "buttons", // no itx
          itx("components.plural.Channel", "Kanäle"),
          undefined,
          false,
        ),
      );
    } else if (device.isPanel) {
      bottomTabItems.push(
        new TabItemData(noItx("buttons"), itx("deviceTemplatePropertieView.function", "Funktionen"), undefined, false),
      );
    } else {
      bottomTabItems.push(
        new TabItemData(noItx("buttons"), itx("deviceTemplatePropertieView.buttons", "Tasten"), undefined, false),
      );
    }
  } else {
    bottomTabItems.push(
      new TabItemData(noItx("channels"), itx("components.plural.Channel", "Kanäle"), undefined, false),
    );
  }

  if (device.isPanel) {
    bottomTabItems.push(
      new TabItemData("panellayout", itx("DeviceTemplatePropertyView.panellayout", "Panel Designer"), undefined, false),
    );
  }

  //
  // MWE 7.5.2026:
  // Zeigen den Verbindungen Tab (massenhaft Taster belegen) nur für Buttons
  // mit mehr als 10 "echten Tasten" an
  // FG 8.05.26 auch für Aktoren insbesondere wichtig für dali
  //
  if (!device.isActor) {
    const realButtonsChannels = device.channels.findAsArray((c) => {
      const c2 = c.tag.substring(1, 2);
      return c2 <= "0" && c2 <= "9" && c.tag.startsWith("T");
    });

    if (realButtonsChannels.length >= 10) {
      bottomTabItems.push(
        new TabItemData(
          "connections", // no itx
          itx("deviceTemplatePropertiesView.connections", "Verbindungen"),
          undefined,
          false,
        ),
      );
    }
  } else {
    bottomTabItems.push(
      new TabItemData(
        "connections", // no itx
        itx("deviceTemplatePropertiesView.connections", "Verbindungen"),
        undefined,
        false,
      ),
    );
  }

  // dont show params and signals if no knx product data is available// for example supply devices
  // or if no template is defined
  let showKnxElements = false;
  if (
    typeof device.template?.knxProductData?.productInfo?.Applications !== "object" &&
    device.template?.knxProductData?.productInfo?.Applications
  ) {
    showKnxElements = true;
  } else if (
    device.template?.knxProductData?.productInfo?.Applications // &&
    // Object.keys(device.template?.knxProductData?.productInfo?.Applications).length > 0
  ) {
    showKnxElements = true;
  }

  if (device.template?.genericParams) {
    showKnxElements = false;
  }

  if (showKnxElements) {
    bottomTabItems.push(
      new TabItemData(
        "params", // no itx
        state.translate("deviceTemplatePropertieView.Parameter", "Parameter"),
        undefined,
        false,
      ),
    );
    bottomTabItems.push(
      new TabItemData(
        "comobjs", // no itx
        state.translate("deviceTemplatePropertieView.signals", "Signale"),
        undefined,
        false,
      ),
    );

    if (device.isActor) {
      bottomTabItems.push(
        new TabItemData(
          "scenes", // no itx
          state.translate("deviceTemplatePropertieView.scenes", "Scenen"),
          undefined,
          false,
        ),
      );
    }

    if (isUiModeDeveloper()) {
      bottomTabItems.push(new TabItemData("KnxDataAnalyser", "KnxDataAnalyser", undefined, false)); // no itx
    }

    if (isUiModeProductAdmin()) {
      bottomTabItems.push(new TabItemData("allSignals", "allSignals", undefined, false)); // no itx
      bottomTabItems.push(new TabItemData("scriptEditor", "Scripts", undefined, false)); // no itx
    }

    // if (isUiModeDeveloper()) {
    //   // check if empty obj   error created on (ABB)JRA/S4.230.5.1 Jal./Rol.Akt.Fahrzt.man.4f,230V,REG
    //   if (
    //     device.template?.manualUrls.size != 0 &&
    //     device.template?.manualUrls.size != undefined
    //   ) {
    //     bottomTabItems.push({
    //       id: "pdfviewer",
    //       title: state.translate(
    //         "deviceTemplatePropertieView.manual",
    //         "Handbuch",
    //       ),
    //     });
    //   }
    // }
  }

  const tabItemData = bottomTabItems.find((i) => i.paneId === activeItem);
  if (tabItemData) {
    tabItemData.setOpen(true, false);
  } else if (bottomTabItems.length > 0) {
    // remembered tab does not exist on this device, fall back to the first tab
    setActiveItem(bottomTabItems[0].paneId);
  }

  const handleSetActiveItem = async (paneId: string) => {
    try {
      if (paneId === "comobjs" || paneId === "buttons" || device.reEvalKnxMetadataNeeded) {
        setLoader(true);
        await device.updateAllFromKnxMetadataImmediately();
        if (!mountedRef.current) return;
      }
      setActiveItem(paneId);
    } catch (ex) {
      logger.error(ex);
    } finally {
      if (mountedRef.current) setLoader(false);
    }
  };

  ///////////////////////////////////// render

  const templateIsSet = device.template != undefined;

  let id = device.id;
  if (device.isDummyDeviceForTemplate && device.template) {
    id = device.template.id;
  }

  return (
    <div
      id={`pane_${id}`}
      className="deviceTemplatePropertiesView"
      style={{
        position: "absolute",
        width: "calc(100% - 10px)",
        height: "calc(100% - 15px)",
        margin: "5px",
        overflow: "hidden",
        borderRadius: "var(--panel-border-radius)",
        boxShadow: "var(--panel-shadow)",

        display: "flex",
        flexDirection: "column",
        textAlign: "left",
        top: "0",
      }}
    >
      <div
        style={{
          position: "relative",
          paddingTop: "5px",
          overflowY: "hidden",
          paddingBottom: "0px",
          height: "40px",
          paddingLeft: "0px",
          paddingRight: "0px",
          backgroundColor: "var(--panel-background)",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            borderBottom: "1px solid orange",
            overflow: "hidden",
            paddingLeft: "10px",
          }}
        >
          <TabStrip items={bottomTabItems} activeItem={activeItem} setActiveItem={handleSetActiveItem} />
        </div>
      </div>
      <div
        ref={contentRef}
        style={{
          position: "relative",
          height: "100%",
          width: "100%",
          overflow: activeItem == "buttons" || activeItem == "channels" ? "hidden" : "auto",
        }}
        // className={loader ? "loading-indicator" : ""}
      >
        {activeItem == "general" && !device.configTemplate && <TabGeneralDevice device={device} />}
        {activeItem == "general" && device.configTemplate && <TabGeneralTemplate device={device} />}
        {activeItem == "params" && templateIsSet && (
          <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onReset={() => {
              setActiveItem("general"); // no itx
            }}
          >
            <ParameterViewerWrapper device={device} />
          </ErrorBoundary>
        )}
        {/* {device.template && activeItem == "channels" && (
          <KnxComObjectTable channels={device.channels} />
        )} */}
        {device.template && activeItem == "comobjs" && <KnxComObjectTable device={device} />}
        {device.template && activeItem == "scenes" && <KnxSceneTable device={device} />}

        {activeItem == "buttons" && <KnxButtonTable button={device} />}
        {activeItem == "channels" && <KnxButtonTable button={device} />}

        {activeItem == "panellayout" && device.isButton && <PanelLayout panel={device as Button} />}

        {activeItem == "pdfviewer" && (
          // <PdfViewer url={device.template?.manualUrls.get("TM")} />
          <PdfViewer url={"xx"} />
        )}
        {device.template && isUiModeDeveloper() && activeItem == "determineTemplateComparbility" && (
          <DetermineTemplateComparbility template={device.template} />
        )}

        {activeItem == "KnxDataAnalyser" && (
          // <PdfViewer url={device.template?.manualUrls.get("TM")} />
          <KnxDataAnalyser device={device} />
        )}
        {device.template && activeItem == "allSignals" && (
          <AllSignalsOfTemplate device={device} isActor={device.isActor} />
        )}
        {device.template && activeItem == "scriptEditor" && <LazyScriptEditor device={device} standAlone={false} />}
        {activeItem == "connections" && <CabinetPropertiesView device={device} asTab />}
      </div>
    </div>
  );
}
